import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Send, Sparkles, Loader2, RotateCcw, Zap } from "lucide-react";
import { PILOT_COPILOT_SUGGESTIONS as COPILOT_SUGGESTIONS } from "../lib/dataPilot";
import { buildDashboardSnapshot } from "../lib/snapshot";
import { useRole } from "../lib/role.jsx";
import { useCopilot } from "../lib/copilot.jsx";
import { useInventoryValuation } from "../lib/valuation";
import { morningBrief } from "../lib/enginesApi";
import { OriginBadge } from "../lib/origin.jsx";
import { cn } from "../lib/utils";
import ReplyText from "./copilot/ReplyText";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "ect-copilot-session-v1";

// Stable uuid per panel mount
function newSessionId() {
  return `ect-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}

function loadOrCreateSessionId() {
  try {
    const existing = localStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const fresh = newSessionId();
    localStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    return newSessionId();
  }
}

export default function CopilotPanel({ open, onClose }) {
  const { role, canAccess, branchScope } = useRole();
  const { pending, clearPending } = useCopilot();
  const valuation = useInventoryValuation(branchScope?.branchId);
  const [sessionId, setSessionId] = useState(() => loadOrCreateSessionId());
  const [messages, setMessages] = useState([
    { role: "assistant", text: `Hi — I'm the 1Rotary AI Copilot, powered by Claude Sonnet 4.5. I'm grounded in live inventory, demand and supply context — Ending Inventory, stock health, the seven engines' recommendations and supplier risk. Every answer is labelled Actual, Synthetic or Mixed by the data it used. Ask me anything, ${role.short}.` },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamedText, setStreamedText] = useState("");
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const lastPendingIdRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streamedText, open]);

  // On first open, restore prior conversation history from Mongo for this session
  useEffect(() => {
    if (!open || historyLoaded || !BACKEND) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${BACKEND}/api/copilot/history/${encodeURIComponent(sessionId)}`);
        if (!resp.ok) return;
        const body = await resp.json();
        if (cancelled) return;
        const msgs = Array.isArray(body.messages) ? body.messages : [];
        if (msgs.length > 0) {
          setMessages(prev => [prev[0], ...msgs.map(m => ({ role: m.role, text: m.text, origin_meta: m.origin_meta, ts: m.ts }))]);
        }
      } catch {
        // best-effort
      } finally {
        if (!cancelled) setHistoryLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [open, sessionId, historyLoaded]);

  function resetSession() {
    if (BACKEND) {
      fetch(`${BACKEND}/api/copilot/reset/${sessionId}`, { method: "POST" }).catch(() => {});
    }
    const fresh = newSessionId();
    try { localStorage.setItem(SESSION_KEY, fresh); } catch {}
    setSessionId(fresh);
    setMessages([{ role: "assistant", text: `Conversation reset. Fresh context for ${role.short}.` }]);
    setStreamedText("");
    setHistoryLoaded(true); // skip refetch for the new empty session
  }

  async function send(q, context) {
    const text = (q ?? input).trim();
    if (!text || streaming) return;
    setMessages(m => [...m, { role: "user", text }]);
    setInput("");
    setStreamedText("");
    setStreaming(true);

    const recs = await morningBrief(branchScope?.branchId).then(b => b.top5 || []).catch(() => []);
    const { text: snapshot, sections } = buildDashboardSnapshot({
      valuation: valuation.loaded ? valuation : null, recs, canAccess, branchId: branchScope?.branchId,
      extra: context ? [context] : [],
    });
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await fetch(`${BACKEND}/api/copilot/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          role_name: role.name,
          snapshot,
          sections,
          message: text,
        }),
        signal: controller.signal,
      });

      if (!resp.ok || !resp.body) {
        const errBody = await resp.text().catch(() => "");
        let detail = "";
        try { const d = JSON.parse(errBody).detail; detail = typeof d === "string" && d.length < 200 ? d : ""; } catch {}
        throw new Error(resp.status === 422
          ? "the question carried more context than I can take in one go — please ask a shorter question"
          : detail || `HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let meta = null;
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE events are delimited by double newline
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const evt = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          // Parse event lines
          const lines = evt.split("\n");
          let evtName = "message";
          let dataLine = "";
          for (const ln of lines) {
            if (ln.startsWith("event:")) evtName = ln.slice(6).trim();
            else if (ln.startsWith("data:")) dataLine += ln.slice(5).trim();
          }
          if (evtName === "token" && dataLine) {
            try {
              const { text: t } = JSON.parse(dataLine);
              acc += t;
              setStreamedText(acc);
            } catch (_) {}
          } else if (evtName === "origin" && dataLine) {
            try { meta = JSON.parse(dataLine); } catch (_) {}
          }
        }
      }

      setMessages(m => [...m, { role: "assistant", text: acc || "(no response)", origin_meta: meta, ts: new Date().toISOString() }]);
      setStreamedText("");
    } catch (e) {
      if (e.name === "AbortError") {
        // user closed/aborted
      } else {
        setMessages(m => [...m, { role: "assistant", text: `⚠ Copilot is unavailable right now: ${e.message}. Try again in a moment.`, error: true }]);
        setStreamedText("");
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }

  useEffect(() => {
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, []);

  // Abort any in-flight stream when the panel is closed
  useEffect(() => {
    if (!open && abortRef.current) {
      abortRef.current.abort();
    }
  }, [open]);

  // Auto-send a pending prompt queued by a page widget (e.g. "Ask Copilot" buttons).
  // Run only after history has been loaded so we don't race the rehydrate.
  useEffect(() => {
    if (!open || !pending || !historyLoaded || streaming) return;
    if (lastPendingIdRef.current === pending.id) return;
    lastPendingIdRef.current = pending.id;
    const text = pending.text;
    const context = pending.context;
    clearPending();
    send(text, context);
  }, [open, pending, historyLoaded, streaming]);

  return (
    <>
      <div
        className={cn("fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] transition-opacity",
          open ? "opacity-100" : "opacity-0 pointer-events-none")}
        onClick={onClose}
      />
      <aside
        data-testid="copilot-panel"
        className={cn(
          "fixed top-0 right-0 h-screen w-full sm:w-[480px] bg-surface border-l border-border z-[70] flex flex-col transition-transform duration-300",
          open ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-sm bg-primary/15 text-primary flex items-center justify-center">
              <Sparkles size={16} />
            </div>
            <div className="leading-tight min-w-0">
              <div className="font-display font-semibold truncate">AI Copilot</div>
              <div className="text-[10px] uppercase tracking-widest text-muted flex items-center gap-1.5">
                <Zap size={9} className="text-success" />
                Claude Sonnet 4.5 · Live
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button data-testid="copilot-reset" onClick={resetSession} title="Reset conversation"
                    className="w-8 h-8 inline-flex items-center justify-center hover:bg-surface-2 rounded-sm text-muted hover:text-fg">
              <RotateCcw size={14} />
            </button>
            <button data-testid="close-copilot" onClick={onClose}
                    className="w-8 h-8 inline-flex items-center justify-center hover:bg-surface-2 rounded-sm">
              <X size={16} />
            </button>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={cn("flex flex-col", m.role === "user" ? "items-end" : "items-start")}>
              <div data-testid={`copilot-msg-${i}`} className={cn(
                "max-w-[88%] rounded-md px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap",
                m.role === "user"
                  ? "bg-primary text-primary-fg"
                  : m.error
                    ? "bg-critical/10 border border-critical/30 text-critical"
                    : "bg-surface-2 border border-border text-fg"
              )}>
                {m.role === "assistant" && !m.error ? <ReplyText text={m.text} /> : m.text}
              </div>
              {m.role === "assistant" && <ReplyOrigin meta={m.origin_meta} ts={m.ts} testid={`copilot-msg-${i}-origin`} />}
            </div>
          ))}
          {streaming && streamedText && (
            <div className="flex justify-start">
              <div className="max-w-[88%] rounded-md px-3.5 py-2.5 text-[13px] leading-relaxed bg-surface-2 border border-border text-fg whitespace-pre-wrap">
                <ReplyText text={streamedText} />
                <span className="inline-block w-1.5 h-3.5 align-text-bottom ml-0.5 bg-primary animate-pulse" />
              </div>
            </div>
          )}
          {streaming && !streamedText && (
            <div className="flex justify-start">
              <div className="rounded-md px-3.5 py-2.5 text-xs leading-relaxed bg-surface-2 border border-border text-muted inline-flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" />
                Thinking…
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-border px-5 py-3">
          <div className="text-[10px] uppercase tracking-widest text-muted mb-2">Suggestions</div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {COPILOT_SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                disabled={streaming}
                onClick={() => send(s)}
                data-testid={`copilot-suggestion-${i}`}
                className={cn(
                  "text-[11px] px-2 py-1 border border-border bg-surface-2 rounded-sm transition-colors",
                  streaming
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-primary/10 hover:border-primary/40 hover:text-primary"
                )}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 border border-border bg-surface-2 rounded-sm pl-3 pr-1.5 h-10">
            <input
              data-testid="copilot-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !streaming && send()}
              disabled={streaming}
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-subtle disabled:opacity-50"
              placeholder={streaming ? "Generating response…" : "Ask the digital twin…"}
            />
            <button
              data-testid="copilot-send"
              onClick={() => send()}
              disabled={streaming || !input.trim()}
              className={cn("h-7 w-7 inline-flex items-center justify-center rounded-sm transition-opacity",
                streaming || !input.trim() ? "bg-surface text-muted cursor-not-allowed" : "bg-primary text-primary-fg hover:opacity-90")}>
              {streaming ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            </button>
          </div>
          <div className="mt-2 text-[10px] text-subtle font-mono-tabular flex items-center justify-between">
            <span>session · {sessionId.slice(0, 18)}…</span>
            <span>{role.name}</span>
          </div>
        </div>
      </aside>
    </>
  );
}

function ReplyOrigin({ meta, ts, testid }) {
  if (!meta) return null;
  if (!meta.origin) {
    return <div data-testid={`${testid}-none`} className="mt-1 text-[10px] text-subtle">General guidance · no dashboard data used</div>;
  }
  const labels = meta.sources.map(s => s.label);
  return (
    <div className="mt-1 flex items-center gap-1.5 text-[10px] text-subtle">
      <OriginBadge testid={testid} origin={meta.origin} updatedAt={ts}
                   source={`AI Copilot · ${meta.inferred ? "full live snapshot (no sections cited)" : `used ${labels.join(", ")}`}`}
                   parts={meta.sources.map(s => ({ origin: s.origin, text: s.label }))} />
      <span data-testid={`${testid}-sources`}>{meta.inferred ? "based on full snapshot" : `based on ${labels.length} data section${labels.length === 1 ? "" : "s"}`}</span>
    </div>
  );
}
